import { useState, useCallback, useRef, useEffect } from 'react';
import { useCases } from '../../../core/di/useCases';
import { useAuth } from '../../../core/providers/AuthProvider';
import { logger } from '../../../shared/utils/logger';
import { InvalidPostDataError } from '../../../use-cases/posts/create/CreatePost';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../shared/hooks/useNotification';

export const useCreatePost = ({ onSuccess, onError } = {}) => {
  const { user } = useAuth();
  const { t } = useTranslation('posts');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const notification = useNotification('posts');
  const createPostUseCase = useRef(useCases.createPost);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const createPost = useCallback(async (postData) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) {
        throw new Error('User must be authenticated to create a post');
      }
      
      const userId = user.uid;
      logger.debug('useCreatePost - Raw post data:', { postData });
      
      const formattedPost = {
        content: postData.content,
        activity: postData.activity,
        visibility: postData.visibility || 'everyone',
        author: {
          uid: user.uid,
          name: user.displayName || 'Anonymous',
          photoURL: user.photoURL
        }
      };
      
      logger.debug('useCreatePost - Formatted post:', formattedPost);
      
      const result = await createPostUseCase.current.execute(formattedPost, userId);
      logger.debug('➕ Added new post:', result);
      
      // Afficher un toast de succès via notre hook personnalisé
      notification.success('toast.post_created_success');
      
      onSuccess?.();
    } catch (error) {
      if (error instanceof InvalidPostDataError) {
        setError({
          field: error.field,
          message: t(`errors.${error.field}`, { defaultValue: error.message })
        });
      } else {
        setError(t('errors.unknown'));
      }
      
      // Afficher un toast d'erreur via notre hook personnalisé
      notification.error('toast.post_created_error');
      
      logger.error('Error creating post:', error);
      onErrorRef.current?.(error);
    } finally {
      setLoading(false);
    }
  }, [user, onSuccess, t, notification]);

  return {
    createPost,
    loading,
    error
  };
}; 